// components/LogsDisplay.js - Fixed PepVPN logs display with alerts (React Native style)
import React, { useState, useMemo, useEffect } from 'react';

const LogsDisplay = ({ 
  pepVPNLogs, 
  firewallLogs, 
  wanLogs, 
  systemLogs, 
  dhcpLogs, 
  adminLogs,
  alerts, 
  vlanAlerts,          
  wanCapAlerts,        
  starlinkAlerts,   
  isClient,
  shouldSkipStarlink = false,
  period = 'daily',
}) => {
  /* ---------------- COLOR SYSTEM ----------------
     Aligned to the Twenty4Seven (t247) dark/mono/square-edge design language
     used in DesktopLayout / MobileLayout. Falls back to the legacy
     --color-* variables so this component still renders sanely anywhere
     the t247 theme vars aren't defined. */
  const COLORS = {
    primary: 'var(--turquoise, var(--color-primary, #88F4EB))',
    secondary: 'var(--navy-700, var(--color-secondary, #16233a))',
    accent: 'var(--neon, var(--color-accent, #CFFF04))',
    background: 'var(--navy-800, var(--color-background, #0B1524))',
    download: 'var(--turquoise, var(--color-download, #88F4EB))',
    muted: 'var(--navy-700, var(--color-muted, #16233a))',
    surface: 'var(--navy-700, var(--color-surface, #16233a))',
    card: 'var(--navy-800, var(--color-card, #0B1524))',
    text: 'var(--text-main, var(--color-text-primary, #E7ECF2))',
    sub: 'var(--text-dim, var(--color-text-secondary, #8B98A9))',
    border: 'var(--line, var(--color-border, rgba(255,255,255,0.08)))',
    success: 'var(--success, var(--color-success, #3FD68C))',
    warning: 'var(--neon, var(--color-warning, #CFFF04))',
    error: 'var(--error, var(--color-error, #FF5C5C))',
    info: 'var(--turquoise, var(--color-download, #88F4EB))',
    starlink: 'var(--color-starlink, #F52795)',
  };

  const FONT = {
    mono: "var(--font-mono, 'Courier New', monospace)",
    sans: 'var(--font-sans, -apple-system, BlinkMacSystemFont, sans-serif)',
  };

  const SPACING = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  };

  // t247 is a square-edge design language — no rounded corners anywhere.
  const RADIUS = {
    sm: 0,
    md: 0,
    lg: 0,
    xl: 0,
    full: 0,
  };

  const TYPOGRAPHY = {
    h8: 14,
    body: 16,
    small: 12,
  };

  /* ---------------- FORMATTERS ---------------- */

  function netmaskToCidr(netmask) {
      // Add validation at the beginning
      if (!netmask || typeof netmask !== 'string') {
          return ''; // or return '/0' depending on your needs
      }
      
      // Convert netmask from dotted decimal to binary string
      const binary = netmask.split('.')
          .map(octet => parseInt(octet, 10).toString(2).padStart(8, '0'))
          .join('');
      
      // Count the number of 1's in the binary string
      const cidr = (binary.match(/1/g) || []).length;
      
      // Validate that it's a valid netmask (all 1's followed by all 0's)
      if (!/^1+0+$/.test(binary)) {
          throw new Error('Invalid netmask: ' + netmask);
      }
      
      return '/' + cidr;
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (period === 'daily') {
        return date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}, ${hours}:${minutes}:${seconds}`;
      }
    } catch (e) {
      if (typeof timestamp === 'string') {
        const timeMatch = timestamp.match(/(\d{1,2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          return `${timeMatch[1]}:${timeMatch[2]}:${timeMatch[3]}`;
        }
        return timestamp.length > 8 ? timestamp.substring(11, 19) : timestamp;
      }
      return 'N/A';
    }
  };

  const parseUTCTimeString = (timestamp) => {
    if (!timestamp) return null;
    
    // If it's already a Date object or number, return it
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'number') return new Date(timestamp * 1000);
    
    // If it's a string with Z or timezone, parse normally
    if (timestamp.includes('Z') || timestamp.includes('+')) {
      return new Date(timestamp);
    }
    
    // Parse UTC time string (assuming format: "YYYY-MM-DD HH:MM:SS")
    try {
      // Replace space with 'T' and add 'Z' to indicate UTC
      const isoString = timestamp.replace(' ', 'T') + 'Z';
      return new Date(isoString);
    } catch (error) {
      console.error('Error parsing time:', timestamp, error);
      return null;
    }
  };

  const formatAlertTime = (alertTime) => {
    const date = parseUTCTimeString(alertTime);
    if (!date || isNaN(date.getTime())) return 'N/A';

    if (period === 'daily') {
      return new Intl.DateTimeFormat('en-GB', {
        //timeZone: 'Europe/Paris',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } else {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}, ${hours}:${minutes}:${seconds}`;
    }
  };

  const getLogDetails = (log) => {
    if (!log) return 'No details';
    
    if (log.detail) return log.detail;
    if (log.message) return log.message;
    if (log.details) return log.details;
    if (log.event_type) return log.event_type;
    if (log.description) return log.description;
    
    try {
      const filteredLog = { ...log };
      delete filteredLog.timestamp;
      delete filteredLog.ts;
      delete filteredLog.time;
      delete filteredLog.device_name;
      delete filteredLog.device_id;
      
      const str = JSON.stringify(filteredLog, null, 2);
      if (str !== '{}') return str;
    } catch (e) {
      // Ignore
    }
    
    return 'No details available';
  };

  /* ---------------- STATUS COLOR UTILITY ---------------- */

  const getStatusColor = (status) => {
    if (!status) return COLORS.warning;
    
    const statusLower = String(status).toLowerCase();
    const greenKeywords = ['online', 'connected', 'ready', 'active', 'running', 'up', 'success', 'complete','normal'];
    const vlanOverageKeywords = ['overage warning'];
    const purpleKeywords = ['overage', 'starlink'];
    const redKeywords = [
      'offline', 'disconnected', 'disabled', 'outage', 
      'failed', 'no active', 'no cable', 'booting', 
      'no device', 'search', 'down', 'unknown', 'error', 'fail'
    ];

    // Check for exact match first
    if (purpleKeywords.includes(statusLower)) return COLORS.starlink;
    if (greenKeywords.includes(statusLower)) return COLORS.success;
    if (redKeywords.includes(statusLower)) return COLORS.error;
    
    // If not exact match, check for partial matches

    for (const keyword of vlanOverageKeywords) {
      if (statusLower.includes(keyword)) {
        return COLORS.error;
      }
    }

    for (const keyword of purpleKeywords) {
      if (statusLower.includes(keyword)) {
        return COLORS.starlink;
      }
    }

    for (const keyword of redKeywords) {
      if (statusLower.includes(keyword)) {
        return COLORS.error;
      }
    }
    
    for (const keyword of greenKeywords) {
      if (statusLower.includes(keyword)) {
        return COLORS.success;
      }
    }
    
    return COLORS.warning;
  };

  const getStatusBackground = (status) => {
    const color = getStatusColor(status);
    // Convert hex to rgba for soft background
    if (color === COLORS.success) return `${COLORS.success}22`;
    if (color === COLORS.error) return `${COLORS.error}22`;
    if (color === COLORS.warning) return `${COLORS.warning}22`;
    if (color === COLORS.starlink) return `${COLORS.starlink}22`;
    return `${COLORS.info}22`;
  };

  /* ---------------- ITEM TYPE BADGE ---------------- */

  const getItemTypeBadge = (alert) => {
    if (!alert || !alert.item_type) return null;

    const itemType = alert.item_type.toLowerCase();
    
    if (itemType === 'wan_interface') {
      return {
        label: 'WAN',
        color: COLORS.primary,
        background: COLORS.secondary,
      };
    }
    
    if (itemType === 'client') {
      return {
        label: 'CLIENT',
        color: COLORS.accent,
        background: 'rgba(202, 251, 109, 0.16)',
      };
    }
    
    if (itemType.includes('starlink')) {
      return {
        label: 'STARLINK',
        color: COLORS.background,
        background: `${COLORS.starlink}`,
      };
    }
    
    return null;
  };

  const getVlanBadge = (alert) => {
    if (!alert) return null;

    return {
      label: 'VLAN',
      color: COLORS.background,
      background: COLORS.download,
    };
  };

  // WAN cap badge — same colour as VLAN (they're both "usage cap"), different label
  const getWanCapBadge = (alert) => {
    if (!alert) return null;

    return {
      label: 'WAN',
      color: COLORS.background,
      background: COLORS.download,
    };
  };

  /* ---------------- ALERT HELPERS ---------------- */

  const getAlertStatus = (status) => {
    const color = getStatusColor(status);
    const text = String(status || '').toLowerCase();

    if (text === 'connected' || text === 'up') {
      return { type: 'success', label: 'UP', color };
    }

    if (text === 'standby') {
      return { type: 'warning', label: 'STANDBY', color };
    }

    if (text === 'disconnected' || text === 'down') {
      return { type: 'error', label: 'DOWN', color };
    }

    return { 
      type: 'info', 
      label: text ? text.toUpperCase() : 'INFO',
      color 
    };
  };

  const getVlanStatus = (alert, status) => {
    let color;

    const text = String(status || '').toLowerCase();

    if (alert.alert_type == 'critical'){
      return { type: 'error', label: text ? text.toUpperCase() : 'VLAN reached max cap', color: COLORS.error  }
    }

    if (alert.alert_type == 'warning'){
      return { type: 'warning', label: text ? text.toUpperCase() : 'VLAN reached warning cap', color: COLORS.warning  }
    }

    return { 
      type: 'info', 
      label: text ? text.toUpperCase() : 'VLAN',
      color: COLORS.warning 
    };
  };

  const getWanCapStatus = (alert, status) => {
    const text = String(status || '').toLowerCase();

    if (alert.alert_type == 'critical'){
      return { type: 'error', label: text ? text.toUpperCase() : 'WAN reached max cap', color: COLORS.error  }
    }

    if (alert.alert_type == 'warning'){
      return { type: 'warning', label: text ? text.toUpperCase() : 'WAN reached warning cap', color: COLORS.warning  }
    }

    return { 
      type: 'info', 
      label: text ? text.toUpperCase() : 'WAN',
      color: COLORS.warning 
    };
  };

  const getAlertIp = (alert) => {
    return (
      alert?.ip ||
      alert?.ip_address ||
      alert?.wan_ip ||
      alert?.public_ip ||
      alert?.private_ip ||
      alert?.address ||
      alert?.interface_ip ||
      null
    );
  };

  const buildAlertData = (alert) => {
    const fromStatus = alert?.from_status || 'unknown';
    const toStatus = alert?.to_status || 'unknown';
    const ip = getAlertIp(alert);
    const itemTypeBadge = getItemTypeBadge(alert);

    return {
      time: formatAlertTime(alert?.alert_time || alert?.timestamp || alert?.ts),
      title: alert.client_name || alert.item_key,
      fromStatus,
      toStatus,
      ip,
      status: getAlertStatus(toStatus),
      itemTypeBadge,
      isAlert: true,
      raw: alert,
    };
  };

  const buildVlanAlertData = (alert) => {
    const fromStatus = 'normal';
    const toStatus = alert.alert_type === 'critical' 
              ? `Overage warning: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)` 
              : `Usage Reminder: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)`;
    const ip = alert.vlan_ip;
    const itemTypeBadge = getVlanBadge(alert);
    const netmask = alert.netmask ? netmaskToCidr(alert.netmask) : '';

    return {
      time: formatAlertTime(alert?.alert_time),
      title: alert.vlan_name,
      fromStatus,
      toStatus,
      ip,
      netmask,
      status: getVlanStatus(alert, toStatus),
      itemTypeBadge,
      isAlert: true,
      raw: alert,
    };
  };

  const buildWanCapAlertData = (alert) => {
    const fromStatus = 'normal';
    const toStatus = alert.alert_type === 'critical' 
              ? `Overage warning: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)` 
              : `Usage Reminder: ${alert.usage_percentage}% (${alert.usage_gb} GB / ${alert.cap_gb} GB)`;
    const ip = alert.wan_ip;
    const itemTypeBadge = getWanCapBadge(alert);

    return {
      time: formatAlertTime(alert?.alert_time),
      title: alert.wan_name || (alert.wan_id != null ? `WAN ${alert.wan_id}` : 'WAN'),
      fromStatus,
      toStatus,
      ip,
      status: getWanCapStatus(alert, toStatus),
      itemTypeBadge,
      isAlert: true,
      raw: alert,
    };
  };

  const buildStarlinkAlertData = (alert) => {
    // Determine the actual from/to status based on alert type
    const isDownloadOverage = alert?.alert_type === 'download_overage';
    const isUploadOverage = alert?.alert_type === 'upload_overage';
    
    let starlinkName = alert.wan_name || 'Starlink';
  
    if (alert.detail) {
      const match = alert.detail.match(/Starlink \((.*?)\)/);
      if (match && match[1]) {
        starlinkName = match[1];
      }
    }

    const fromStatus = 'normal';
    const toStatus = isDownloadOverage ? 'download_overage' : 'upload_overage';
    
    // Get appropriate status color based on overage type
    const getStarlinkStatusColor = (status) => {
      if (status === 'download_overage' || status === 'upload_overage') {
        return { type: 'warning', label: 'OVERAGE', color: COLORS.starlink };
      }
      return { type: 'info', label: status.toUpperCase(), color: COLORS.info };
    };
    
    const itemTypeBadge = {
      label: 'STARLINK',
      color: COLORS.background,
      background: `${COLORS.starlink}`,
    };

    return {
      time: formatAlertTime(alert?.alert_time || alert?.timestamp || alert?.ts),
      title: starlinkName,
      fromStatus,
      toStatus,
      ip: null,
      status: getStarlinkStatusColor(toStatus),
      itemTypeBadge,
      isAlert: true,
      raw: alert,
      details: isDownloadOverage 
        ? 'Download bandwidth overage' 
        : 'Upload bandwidth overage',
    };
  };

  /* ---------------- STATUS ANALYZER ---------------- */

  const analyzeLog = (details = '') => {
    const text = String(details).toLowerCase();

    if (text.includes('error') || text.includes('fail')) {
      return { type: 'error', label: 'ERROR' };
    }

    if (text.includes('complete') || text.includes('success')) {
      return { type: 'success', label: 'SUCCESS' };
    }

    if (text.includes('warning')) {
      return { type: 'warning', label: 'WARNING' };
    }

    return { type: 'info', label: 'INFO' };
  };

  /* ---------------- KEYWORD HIGHLIGHT ---------------- */

  const highlightKeywords = (text = '') => {
    const keywordMap = {
      error: COLORS.error,
      fail: COLORS.error,
      success: COLORS.success,
      complete: COLORS.success,
      warning: COLORS.warning,
    };

    const regex = /\b(error|fail|success|complete|warning)\b/gi;
    const parts = String(text).split(regex);

    return parts.map((part, index) => {
      const lower = part.toLowerCase();

      if (keywordMap[lower]) {
        return (
          <span
            key={index}
            style={{
              color: keywordMap[lower],
              fontWeight: 700,
            }}
          >
            {part}
          </span>
        );
      }

      return <span key={index}>{part}</span>;
    });
  };

  /* ---------------- NORMALIZE LOGS ---------------- */

  const normalizeLogs = (logs) => {
    if (!logs) return [];

    if (Array.isArray(logs)) return logs;

    if (logs.data && Array.isArray(logs.data)) return logs.data;

    if (logs.logs && Array.isArray(logs.logs)) return logs.logs;

    if (typeof logs === 'object') return Object.values(logs);

    return [];
  };

  /* ---------------- HELPERS ---------------- */

  const hasLogs = (data) => normalizeLogs(data).length > 0;
  const countLogs = (data) => normalizeLogs(data).length;

  // Build tabs array (includes all possible tabs, some may be disabled)
  const tabs = [
    {
      id: 'pepvpv',
      label: 'PepVPN',
      enabled: hasLogs(pepVPNLogs),
      count: countLogs(pepVPNLogs),
    },
    {
      id: 'wan',
      label: 'WAN',
      enabled: hasLogs(wanLogs),
      count: countLogs(wanLogs),
    },
    {
      id: 'system',
      label: 'System',
      enabled: hasLogs(systemLogs),
      count: countLogs(systemLogs),
    },
    {
      id: 'dhcp',
      label: 'DHCP',
      enabled: hasLogs(dhcpLogs),
      count: countLogs(dhcpLogs),
    },
    ...(!isClient
      ? [
          {
            id: 'admin',
            label: 'Admin',
            enabled: hasLogs(adminLogs),
            count: countLogs(adminLogs),
          },
        ]
      : []),
    {
      id: 'alerts',
      label: 'Alerts',
      enabled: Array.isArray(alerts) && alerts.length > 0,
      count: alerts?.length || 0,
    },

    {
      id: 'cap-alerts',
      label: 'Cap',
      enabled: (Array.isArray(vlanAlerts) && vlanAlerts.length > 0) ||
               (Array.isArray(wanCapAlerts) && wanCapAlerts.length > 0),
      count: (vlanAlerts?.length || 0) + (wanCapAlerts?.length || 0),
    },

    // Only include Starlink tab if shouldSkipStarlink is false AND there are Starlink alerts
    ...(!shouldSkipStarlink && Array.isArray(starlinkAlerts) && starlinkAlerts.length > 0
      ? [{
          id: 'starlink-alerts',
          label: 'Starlink',
          enabled: true,
          count: starlinkAlerts?.length || 0,
        }]
      : []),
  ];

  // Filter to only show enabled tabs (tabs with content)
  const visibleTabs = tabs.filter(tab => tab.enabled);

  // Find the first available enabled tab's id
  const getFirstAvailableTabId = () => {
    const firstEnabledTab = visibleTabs[0];
    return firstEnabledTab ? firstEnabledTab.id : 'pepvpv';
  };

  // Initialize activeTab state with first available tab
  const [activeTab, setActiveTab] = useState(getFirstAvailableTabId);

  // Update active tab if current tab becomes disabled
  useEffect(() => {
    const currentTabExists = visibleTabs.some(tab => tab.id === activeTab);
    if (!currentTabExists && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  /* ---------------- GET LOGS ---------------- */

  const getCurrentLogs = () => {
    let logs;

    switch (activeTab) {
      case 'pepvpv':
        logs = normalizeLogs(pepVPNLogs)
          .map((log) => {
            const details = getLogDetails(log);
            return {
              time: formatTimestamp(log.ts || log.datetime || log.timestamp),
              details,
              status: analyzeLog(details),
              isAlert: false,
            };
          })
          .filter((l) => l.details !== 'No details available');
        break;

      case 'wan':
        logs = normalizeLogs(wanLogs)
          .map((log) => {
            const details = getLogDetails(log);
            return {
              time: formatTimestamp(log.ts || log.datetime || log.timestamp),
              details,
              status: analyzeLog(details),
              isAlert: false,
            };
          })
          .filter((l) => l.details !== 'No details available');
        break;

      case 'system':
        logs = normalizeLogs(systemLogs)
          .map((log) => {
            const details = getLogDetails(log);
            return {
              time: formatTimestamp(log.ts || log.datetime || log.timestamp),
              details,
              status: analyzeLog(details),
              isAlert: false,
            };
          })
          .filter((l) => l.details !== 'No details available');
        break;

      case 'dhcp':
        logs = normalizeLogs(dhcpLogs)
          .map((log) => {
            const details = getLogDetails(log);
            return {
              time: formatTimestamp(log.ts || log.datetime || log.timestamp),
              details,
              status: analyzeLog(details),
              isAlert: false,
            };
          })
          .filter((l) => l.details !== 'No details available');
        break;

      case 'admin':
        logs = normalizeLogs(adminLogs)
          .map((log) => {
            const details = getLogDetails(log);
            return {
              time: formatTimestamp(log.ts || log.datetime || log.timestamp),
              details,
              status: analyzeLog(details),
              isAlert: false,
            };
          })
          .filter((l) => l.details !== 'No details available');
        break;

      case 'alerts':
        logs = Array.isArray(alerts) ? alerts.map(buildAlertData) : [];
        break;

      case 'cap-alerts': {
        // Combine VLAN caps and WAN caps into one "Cap" view, sorted newest first.
        const vlanItems = Array.isArray(vlanAlerts) ? vlanAlerts.map(buildVlanAlertData) : [];
        const wanItems = Array.isArray(wanCapAlerts) ? wanCapAlerts.map(buildWanCapAlertData) : [];
        logs = [...vlanItems, ...wanItems].sort((a, b) => {
          const da = parseUTCTimeString(a.raw?.alert_time);
          const db = parseUTCTimeString(b.raw?.alert_time);
          return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
        });
        break;
      }

      case 'starlink-alerts':
        logs = Array.isArray(starlinkAlerts)
          ? starlinkAlerts.map(buildStarlinkAlertData)
          : [];
        break;

      default:
        logs = [];
    }

    return logs;
  };

  const currentLogs = useMemo(getCurrentLogs, [
    activeTab,
    pepVPNLogs,
    wanLogs,
    systemLogs,
    dhcpLogs,
    adminLogs,
    alerts,
    vlanAlerts,
    wanCapAlerts,
    starlinkAlerts,
  ]);

  const hasAnyLogs = visibleTabs.length > 0;

  const renderLogItem = (item, index) => {
    if (item.isAlert) {
      return (
        <div key={index} className="log-row">
          <div
            className="status-bar"
            style={{ backgroundColor: item.status.color, marginRight: '8px' }}
          />

          <div className="log-content">
            <div className="log-header">
              <span className="log-time">{item.time}</span>

              <div className="header-badges">
                {item.itemTypeBadge && (
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: item.itemTypeBadge.background,
                      color: item.itemTypeBadge.color,
                    }}
                  >
                    {item.itemTypeBadge.label}
                  </span>
                )}
              </div>
            </div>

            <div className="alert-title">
              {item.title}
              {item.ip && <span> ({item.ip}{item.netmask || ''})</span>}
            </div>

            <div className="alert-state-row">
              <span
                className="alert-state-badge"
                style={{
                  backgroundColor: getStatusBackground(item.fromStatus),
                  color: getStatusColor(item.fromStatus),
                }}
              >
                {String(item.fromStatus || 'unknown').toUpperCase()}
              </span>

              <span className="alert-arrow">→</span>

              <span
                className="alert-state-badge"
                style={{
                  backgroundColor: getStatusBackground(item.toStatus),
                  color: getStatusColor(item.toStatus),
                }}
              >
                {item.details ? item.details : String(item.toStatus || 'unknown').toUpperCase()}
              </span>
            </div>

          </div>
        </div>
      );
    }

    // Regular log item
    return (
      <div key={index} className="log-row">
        <div className={`status-bar ${item.status.type}-bar`} style={{ marginRight: '8px' }} />

        <div className="log-content">
          <div className="log-header">
            <span className="log-time">{item.time}</span>

            <span className={`status-badge ${item.status.type}-badge`}>
              {item.status.label}
            </span>
          </div>

          <div className="log-details">
            {typeof item.details === 'string'
              ? highlightKeywords(item.details)
              : item.details}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="logs-display">
      {/* Tabs */}
      <div className="tabs-scroll">
        <div className="tabs-row">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={[
                'tab-button',
                activeTab === tab.id ? 'tab-active' : '',
              ].join(' ')}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-text">{tab.label}</span>

              {tab.count > 0 && (
                <span className="count-badge">
                  <span className="count-text">{tab.count}</span>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Logs List */}
      {!hasAnyLogs ? (
        <div className="empty-state">
          <span className="muted">No logs available</span>
        </div>
      ) : !currentLogs.length ? (
        <div className="empty-state">
          <span className="muted">No logs in this category</span>
        </div>
      ) : (
        <div className="log-list">
          {currentLogs.slice(0, 350).map(renderLogItem)}

          {currentLogs.length > 350 && (
            <div className="footer-text">
              Showing 350 of {currentLogs.length} logs
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .logs-display {
          background: transparent;
          border-radius: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          min-width: 0;
          font-family: ${FONT.sans};
        }

        /* Tabs — square, mono, turquoise-active (matches .t247-logs-tab) */
        .tabs-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          margin-bottom: 0;
          flex-shrink: 0;
        }

        .tabs-scroll::-webkit-scrollbar {
          display: none;
        }

        .tabs-row {
          display: flex;
          flex-wrap: nowrap;
          background: transparent;
          border-radius: 0;
          padding: 0;
          margin: 0 0 ${SPACING.sm}px 0;
          gap: 2px;
          min-width: max-content;
        }

        .tab-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 13px;
          border-radius: 0;
          margin-right: 0;
          min-width: 0;
          min-height: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          flex: 0 0 auto;
          transition: background-color 0.15s ease, color 0.15s ease;
        }

        .tab-button:last-child {
          margin-right: 0;
        }

        .tab-active {
          background: ${COLORS.primary};
          box-shadow: none;
        }

        .tab-text {
          font-family: ${FONT.mono};
          font-size: 10.5px;
          letter-spacing: 0.08em;
          color: ${COLORS.sub};
          font-weight: 400;
          line-height: 1;
          white-space: nowrap;
          text-transform: uppercase;
        }

        .tab-active .tab-text {
          color: ${COLORS.background};
          font-weight: 700;
        }

        .count-badge {
          background: transparent;
          border-radius: 0;
          padding: 0;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .count-text {
          font-family: ${FONT.mono};
          font-size: 10.5px;
          letter-spacing: 0.04em;
          font-weight: 400;
          color: ${COLORS.sub};
          text-align: center;
          line-height: 1;
        }

        .tab-active .count-text {
          color: ${COLORS.background};
        }

        /* Log List — thin hairline rows, no cards, no shadows */
        .log-list {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          max-height: 350px;
          border-top: 1px solid ${COLORS.border};
        }

        .log-row {
          display: flex;
          flex-direction: row;
          background: transparent;
          padding: 11px ${SPACING.md}px;
          border-bottom: 1px solid ${COLORS.border};
          transition: background-color 0.15s ease;
        }

        .log-row:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .status-bar {
          width: 3px;
          border-radius: 0;
          flex-shrink: 0;
          margin-right: ${SPACING.xs}px;
        }

        .error-bar {
          background-color: ${COLORS.error};
        }

        .success-bar {
          background-color: ${COLORS.success};
        }

        .warning-bar {
          background-color: ${COLORS.warning};
        }

        .info-bar {
          background-color: ${COLORS.info};
        }

        .log-content {
          flex: 1;
          min-width: 0;
        }

        .log-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: ${SPACING.sm}px;
          margin-bottom: 6px;
        }

        .header-badges {
          display: flex;
          align-items: center;
          gap: ${SPACING.xs}px;
          flex-shrink: 0;
        }

        .log-time {
          font-family: ${FONT.mono};
          font-size: 10.5px;
          color: ${COLORS.sub};
          flex-shrink: 0;
          padding-top: 1px;
        }

        .status-badge {
          padding: 1px 6px;
          height: 15px;
          border-radius: 0;
          font-family: ${FONT.mono};
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.08em;
          color: ${COLORS.background};
          line-height: 15px;
          white-space: nowrap;
          flex-shrink: 0;
          text-transform: uppercase;
        }

        .error-badge {
          background-color: ${COLORS.error};
        }

        .success-badge {
          background-color: ${COLORS.success};
        }

        .warning-badge {
          background-color: ${COLORS.warning};
          color: ${COLORS.background};
        }

        .info-badge {
          background-color: ${COLORS.info};
        }

        .log-details {
          font-size: 11.5px;
          color: ${COLORS.sub};
          font-family: ${FONT.mono};
          line-height: 1.5;
          word-break: break-word;
        }

        .alert-title {
          font-size: 12px;
          color: ${COLORS.text};
          font-family: ${FONT.sans};
          font-weight: 700;
          margin-bottom: ${SPACING.xs}px;
          word-break: break-word;
        }

        .alert-state-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: ${SPACING.xs}px;
          margin-bottom: ${SPACING.xs}px;
        }

        .alert-arrow {
          font-family: ${FONT.mono};
          font-size: 12px;
          color: ${COLORS.sub};
          font-weight: 400;
          margin: 0 2px;
        }

        .alert-state-badge {
          padding: 3px 7px;
          border-radius: 0;
          font-family: ${FONT.mono};
          font-size: 10px;
          letter-spacing: 0.06em;
          font-weight: 400;
          line-height: 1.2;
          white-space: nowrap;
          display: inline-block;
          text-transform: uppercase;
        }

        .alert-meta {
          font-size: 10.5px;
          color: ${COLORS.sub};
          margin-top: 2px;
          font-family: ${FONT.mono};
        }

        .alert-meta-value {
          color: ${COLORS.text};
          font-weight: 600;
        }

        .empty-state {
          padding: ${SPACING.xl}px;
          text-align: center;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .muted {
          font-family: ${FONT.mono};
          font-size: 11px;
          letter-spacing: 0.04em;
          color: ${COLORS.sub};
          text-transform: uppercase;
        }

        .footer-text {
          text-align: center;
          padding: ${SPACING.sm}px;
          font-family: ${FONT.mono};
          font-size: 9.5px;
          letter-spacing: 0.05em;
          color: ${COLORS.sub};
          border-top: 1px solid ${COLORS.border};
          text-transform: uppercase;
        }
      `}</style>
    </div>
  );
};

export default LogsDisplay;
